import { InsightsNavbar } from "@/components/InsightsNavbar";
import { FounderQuote } from "@/components/FounderQuote";
import { AnimatedSection } from "@/components/AnimatedSection";
import { InsightsLeadModal } from "@/components/InsightsLeadModal";
import { ScrollProgressIndicator } from "@/components/ScrollProgressIndicator";
import { Footer } from "@/components/Footer";
import { SummerHeroSection } from "@/components/insights/summer/SummerHeroSection";
import { InsightsBreadcrumb } from "@/components/InsightsBreadcrumb";
import { InsightSEO } from "@/hooks/useInsightSEO";
import { RelatedTools } from "@/components/RelatedTools";
import { InsightEngagementTracker } from "@/components/InsightEngagementTracker";
import { InsightFAQSection } from "@/components/InsightFAQSection";
import { InsightBenchmarks } from "@/components/InsightBenchmarks";
import { TableOfContents } from "@/components/TableOfContents";
import { SimpleCard } from "@/components/DataCard";
import { MapPin, CheckCircle, ArrowRight } from "lucide-react";

const SummerTakeawayBanner = () => (
  <div className="relative overflow-hidden bg-gradient-to-r from-success/5 via-success/10 to-success/5 border border-success/20 rounded-2xl p-5 sm:p-6">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-24 bg-success/10 blur-3xl" />
    <div className="relative flex items-start gap-4">
      <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-success/15 flex items-center justify-center">
        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-success" />
      </div>
      <div className="flex-1">
        <h2 className="font-display text-lg sm:text-xl font-semibold text-foreground mb-2">
          The Bottom Line
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
          Summer email dips between mid-June and late August are{" "}
          <span className="font-semibold text-foreground">normal and expected</span>.
          They are driven by vacations, out-of-office coverage, and reduced business
          activity — not your deliverability setup. This data helps you set realistic
          expectations and focus on what actually matters.
        </p>
        <div className="flex items-center gap-2 mt-3 text-sm text-success font-medium">
          <ArrowRight className="w-4 h-4" />
          <span>Scroll down for the full data breakdown</span>
        </div>
      </div>
    </div>
  </div>
);

const SummerKeyFindings = () => (
  <section className="section-card mt-5 sm:mt-7">
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1.5 sm:gap-2 mb-5 sm:mb-6">
      <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-foreground">
        What the data actually shows
      </h2>
      <p className="text-[11px] sm:text-xs text-muted-foreground">
        Summer engagement patterns across verified sources
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
      <SimpleCard title="1. Engagement softens in July and August">
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          Most industry coverage (HubSpot, Seguno, Demand Gen Report) describes July as a
          calendar "lull" and August as the deepest B2B trough — driven by vacations, not
          deliverability. We avoid quoting a precise open-rate delta because Apple Mail
          Privacy Protection (2021) inflates opens and makes month-over-month comparisons
          unreliable.
        </p>
        <ul className="list-none space-y-1.5">
          {[
            "July: marketers describe a calendar lull (Seguno)",
            "August: deeper B2B slowdown, especially in Europe",
            "September: engagement recovers as offices reopen",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </SimpleCard>

      <SimpleCard title="2. Out-of-office replies tick up — but less than people assume">
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          ZoomInfo analysed millions of outbound emails and found the Jun–Aug
          out-of-office reply rate sits around <span className="font-semibold text-foreground">3%</span>,
          only slightly above the yearly median of ~2.7%. The real spike is Christmas week
          (~6.8%). Reply timing stretches anecdotally — buyers really are away — but we don't
          have a reliable industry figure for "time to first reply", so we don't quote one.
        </p>
        <ul className="list-none space-y-1.5">
          {[
            "ZoomInfo Jun–Aug OOO rate: ~3%",
            "Yearly median: ~2.7%",
            "Christmas week peak: ~6.8%",
            "Europe trends higher than US in August",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </SimpleCard>

      <SimpleCard title="3. Inbox placement stays healthy — it's not deliverability">
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          Independent inbox placement benchmarks do not show a summer spike in spam-folder
          rates. Senders who maintained healthy authentication (SPF/DKIM/DMARC), low complaint
          rates, and good list hygiene continue to land in the inbox. The summer dip is
          recipient attention, not sender reputation.
        </p>
        <ul className="list-none space-y-1.5">
          {[
            "Auth and complaint thresholds don't change with the season",
            "If deliverability indicators are healthy, the dip is behavioural",
            "Watch reply rate and meetings booked, not just opens",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </SimpleCard>

      <SimpleCard title="4. B2B is hit harder than B2C and ecommerce">
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          Ecommerce and travel brands often see summer engagement hold steady or even rise 
          (holiday bookings, summer sales). B2B and SaaS outreach feels the slowdown most
          because the buyers themselves are on leave. The magnitude varies a lot by dataset,
          so we describe direction rather than precise percentages.
        </p>
        <ul className="list-none space-y-1.5">
          {[
            "B2B / SaaS: most affected — buyers are away",
            "Ecommerce: often flat or up (summer sale calendar)",
            "Travel / Hospitality: peak booking season",
            "Newsletters: mild general dip",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="w-1 h-1 rounded-full bg-warning mt-1.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </SimpleCard>
    </div>
  </section>
);

const SummerRegionalComparison = () => (
  <section className="section-card mt-5 sm:mt-7">
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1.5 sm:gap-2 mb-5 sm:mb-6">
      <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-foreground">
        Regional differences: US vs Europe
      </h2>
      <p className="text-[11px] sm:text-xs text-muted-foreground">
        Holiday norms shape how deep the dip goes
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
      <SimpleCard title="United States">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-foreground">July 4th week + scattered PTO</span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          US workers typically take shorter, scattered vacations. The deepest dip is around 
          Independence Day (July 4) and the week after. Many remain partially reachable. 
          August is lighter than July but not as quiet as Southern Europe.
        </p>
        <ul className="list-none space-y-1.5">
          {[
            "Peak dip: July 1–15 (Independence Day + PTO)",
            "Typical vacation: 3–7 days, often stay-cations",
            "Recovery begins: late August, full after Labor Day (1st Mon of Sept)"
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </SimpleCard>

      <SimpleCard title="Europe">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-foreground">August shutdown culture</span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          Many European countries — France, Italy, Spain, Germany, Scandinavia — have a strong 
          August holiday culture where entire offices close for 2–4 weeks. Decision makers 
          are genuinely unreachable. Qualitatively this is the deepest B2B engagement trough
          of the year, though precise country-level percentages are not well sourced.
        </p>
        <ul className="list-none space-y-1.5">
          {[
            "Peak dip: August 1–31 (full-month pattern)",
            "Typical vacation: 2–4 consecutive weeks",
            "Recovery begins: early September, full by mid-September"
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </SimpleCard>
    </div>
  </section>
);

const SummerPracticalSection = () => (
  <section className="section-card mt-5 sm:mt-7">
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1.5 sm:gap-2 mb-5 sm:mb-6">
      <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-foreground">
        What this means for your summer campaigns
      </h2>
      <p className="text-[11px] sm:text-xs text-muted-foreground">
        How to interpret and adapt to summer performance fairly
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
      <SimpleCard title="1. A summer dip is normal, not a red flag">
        <p className="text-xs text-muted-foreground leading-relaxed">
          When replies and engagement drop between mid-June and late August, this aligns 
          with global seasonal patterns. Deliverability metrics (inbox rate, spam rate, 
          bounce rate) should remain stable. If those are healthy, the dip is attention-based, 
          not infrastructure-based.
        </p>
      </SimpleCard>

      <SimpleCard title="2. Adjust send volume and expectations">
        <ul className="list-none space-y-1.5">
          {[
            "Reduce non-essential campaign frequency by 20–30%",
            "Move major product launches to September–October",
            "Avoid the week of July 4th and mid-August for US lists",
            "Avoid all of August for heavily European B2B lists"
          ].map((practice, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="w-1 h-1 rounded-full bg-success mt-1.5 shrink-0" />
              {practice}
            </li>
          ))}
        </ul>
      </SimpleCard>

      <SimpleCard title="3. Lean into async-friendly formats">
        <ul className="list-none space-y-1.5">
          {[
            "Send digest-style roundups people can scan later",
            "Include 'read when you are back' offers with later deadlines",
            "Use longer-form content that ages well (guides, reports)",
            "Schedule nurture sequences to resume in September"
          ].map((practice, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="w-1 h-1 rounded-full bg-success mt-1.5 shrink-0" />
              {practice}
            </li>
          ))}
        </ul>
      </SimpleCard>

      <SimpleCard title="4. How we evaluate success">
        <p className="text-xs text-muted-foreground leading-relaxed">
          We compare your campaigns against realistic seasonal benchmarks rather than treating 
          July–August in isolation. This helps separate structural summer effects from genuine 
          issues like list quality, content relevance, or infrastructure problems. If your 
          deliverability indicators are healthy, a softer summer in replies and meetings booked 
          is expected, not alarming.
        </p>
      </SimpleCard>
    </div>

    {/* Sources */}
    <div className="mt-6 sm:mt-8 pt-5 border-t border-border">
      <h3 className="font-display text-sm font-semibold text-foreground mb-3">
        Sources and further reading
      </h3>
      <div className="space-y-1.5 text-[10px] sm:text-[11px] text-muted-foreground">
        <p className="leading-relaxed">
          • ZoomInfo – Analysis of millions of outbound emails showing out-of-office reply rates spike across summer:{" "}
          <a href="https://pipeline.zoominfo.com/sales/out-of-office-reply-rates" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline transition-colors">Out-of-Office Reply Rates by Season</a>.
        </p>
        <p className="leading-relaxed">
          • HubSpot – Marketing leaders on preparing for, navigating, and thriving through the summer slump:{" "}
          <a href="https://blog.hubspot.com/marketing/summer-slump-growth-tips" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline transition-colors">How to Survive the Summer Slump</a>.
        </p>
        <p className="leading-relaxed">
          • Seguno – Why July is a calendar "lull" for email marketers and how brands can beat the summer sales slump:{" "}
          <a href="https://www.seguno.com/blog/beat-the-summer-slump-with-5-july-newsletter-ideas" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline transition-colors">Beat the Summer Slump (July)</a>.
        </p>
        <p className="leading-relaxed">
          • Firework – Summer engagement rates drop as subscribers trade screens for beach time:{" "}
          <a href="https://webflow.firework.com/blog/summer-email-marketing-tips" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline transition-colors">Summer Email Marketing — Engagement Reality</a>.
        </p>
        <p className="leading-relaxed">
          • Demand Gen Report – Seasonal guide to B2B buyer engagement, including mid-summer dips and recovery windows:{" "}
          <a href="https://www.demandgenreport.com/industry-news/the-seasonal-guide-to-connecting-with-b2b-buyers/48350/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline transition-colors">Seasonal Guide to B2B Buyers</a>.
        </p>
      </div>
      <p className="mt-4 text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
        Every source above is specifically about the summer engagement dip (June–August) —
        not generic year-round benchmarks. They consistently show the slowdown is driven by
        vacations, out-of-office coverage, and reduced business activity, not deliverability failure.
      </p>
    </div>
  </section>
);

const SummerEmailDrop = () => {
  return (
    <>
      <InsightSEO 
        slug="summer-email-drop" 
        additionalKeywords={["summer email", "holiday email", "seasonal email", "August email performance", "B2B summer outreach"]}
      />
      <InsightEngagementTracker slug="summer-email-drop" title="Summer Email Drop" />
      <InsightsNavbar showBack />
      <ScrollProgressIndicator />
      <TableOfContents />
      <InsightsLeadModal />
      <main className="page-container pb-16 sm:pb-20">
        <InsightsBreadcrumb 
          category="Seasonal Analysis" 
          currentPage="Summer Email Engagement Drop" 
        />
        <SummerHeroSection />
        
        <AnimatedSection delay={100} className="mt-10 sm:mt-12">
          <SummerTakeawayBanner />
        </AnimatedSection>

        <AnimatedSection delay={50} className="mt-8 sm:mt-10">
          <FounderQuote
            quote="My honest take heading into summer: from mid-June to late August, buyers in the US and Europe are on leave, so replies slow and follow-ups sit longer than usual. Your campaigns are not broken — it is the calendar, and things pick back up in September."
          />
        </AnimatedSection>

        <div className="section-divider" />

        <AnimatedSection delay={0}>
          <SummerKeyFindings />
        </AnimatedSection>
        
        <AnimatedSection delay={0}>
          <SummerRegionalComparison />
        </AnimatedSection>
        
        <AnimatedSection delay={0}>
          <SummerPracticalSection />
        </AnimatedSection>
        
        {/* Benchmark Tables */}
        <AnimatedSection delay={0}>
          <InsightBenchmarks insightSlug="summer-email-drop" />
        </AnimatedSection>
        
        {/* FAQ Section */}
        <AnimatedSection delay={0}>
          <InsightFAQSection insightSlug="summer-email-drop" />
        </AnimatedSection>
        
        {/* Related Tools Section */}
        <AnimatedSection delay={0}>
          <RelatedTools insightSlug="summer-email-drop" />
        </AnimatedSection>
      </main>
      <Footer />
    </>
  );
};

export default SummerEmailDrop;
